// Copyright (c) Said Borna. All rights reserved.
import { Navigate, useLocation } from 'react-router-dom';
import type { ReactElement } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { isStaffRole } from '@/types';

interface ProtectedRouteProps {
    /** The content to render if authorized. */
    readonly children: ReactElement;
    /** If true, only staff roles can access this route. */
    readonly staffOnly?: boolean;
}

/**
 * Route guard that redirects unauthenticated users to login.
 * Optionally restricts access to staff-only roles.
 */
export function ProtectedRoute({ children, staffOnly = false }: ProtectedRouteProps): ReactElement {
    const { user, isLoading } = useAuth();
    const location = useLocation();

    if (isLoading) {
        return <LoadingScreen />;
    }

    if (!user) {
        return <Navigate to="/" state={{ from: location }} replace />;
    }

    if (staffOnly && !isStaffRole(user.role)) {
        return <Navigate to="/dashboard" replace />;
    }

    return children;
}

/**
 * Full-screen loading indicator for auth state resolution.
 * Enterprise design: clean shimmer, no spinners.
 */
function LoadingScreen(): ReactElement {
    return (
        <div className="flex h-screen w-screen items-center justify-center bg-[#FAFAFA]">
            <div className="flex flex-col items-center gap-3">
                <div className="h-6 w-6 animate-spin rounded-full border-2 border-foreground border-t-transparent" />
                <p className="text-sm text-muted-foreground">Loading...</p>
            </div>
        </div>
    );
}
